import { useEffect, useRef, useState } from "react";
import { Download, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { absUrl, getSessionId, trackPreview } from "./previewApi";

export const LeadSheetCard = ({ sheet, songTitle }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(false);
  const img = absUrl(sheet.image_url);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { trackPreview("sheet_view"); io.disconnect(); }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const openZoom = () => { setOpen(true); setZoom(false); trackPreview("sheet_zoom"); };
  const download = () => {
    trackPreview("sheet_download");
    window.location.href = `${absUrl(sheet.pdf_url)}&sid=${encodeURIComponent(getSessionId())}`;
  };
  const alt = `Legal Lead Sheetz study chart for ${songTitle}`;

  return (
    <section ref={ref} className="rounded-3xl border border-border bg-background p-4 sm:p-6 shadow-sm" data-testid="lead-sheet-card">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 px-1">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-primary">Companion study chart</p>
          <h2 className="mt-1 font-serif text-2xl font-bold leading-tight">Legal Lead Sheetz<sup className="text-sm">™</sup></h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openZoom} className="rounded-full" data-testid="lead-sheet-expand-btn">
            <Maximize2 className="mr-1.5 h-4 w-4" /> Full view
          </Button>
          <Button size="sm" onClick={download} className="rounded-full" data-testid="lead-sheet-download-btn">
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
      </div>
      <button type="button" onClick={openZoom} className="group block w-full overflow-hidden rounded-2xl border border-border bg-secondary/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/30" aria-label="Open Lead Sheetz full view" data-testid="lead-sheet-image-btn">
        <img src={img} alt={alt} width={sheet.width} height={sheet.height} className="h-auto w-full transition-transform duration-500 group-hover:scale-[1.01]" loading="lazy" data-testid="lead-sheet-image" />
      </button>
      <p className="mt-3 px-1 text-xs text-muted-foreground">Tap the chart to zoom. Downloads are marked as coming from barsongz.com.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[92vh] max-w-5xl flex-col gap-3 p-3 sm:p-5" data-testid="lead-sheet-dialog">
          <div className="flex items-center justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="font-serif text-lg">Legal Lead Sheetz™</DialogTitle>
              <DialogDescription className="text-xs">{songTitle}</DialogDescription>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setZoom((z) => !z)} data-testid="lead-sheet-zoom-toggle">
              {zoom ? <ZoomOut className="mr-1.5 h-4 w-4" /> : <ZoomIn className="mr-1.5 h-4 w-4" />}
              {zoom ? "Fit" : "Zoom"}
            </Button>
          </div>
          <div className="flex-1 overflow-auto rounded-xl bg-secondary/40" data-testid="lead-sheet-dialog-scroll">
            <img src={img} alt={alt} className={zoom ? "h-auto w-[220%] max-w-none sm:w-[160%]" : "mx-auto h-full w-auto max-w-full object-contain"} />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
